import '../css/style.css';
import 'remixicon/fonts/remixicon.css';

import Swiper from 'swiper';
import { Controller, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/controller';

import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';

window.Alpine = Alpine;
Alpine.plugin(collapse);

Alpine.data('testimonials', () => ({
  swiper: null,

  init() {
    const swiper = this.$el.querySelector('.swiper');
    const prevBtn = this.$el.querySelector('[data-nav-button="prev"]');
    const nextBtn = this.$el.querySelector('[data-nav-button="next"]');
    const paginationBullets = this.$el.querySelector('.pagination-bullets');
    const paginationFraction = this.$el.querySelector('.pagination-fraction');

    this.swiper = new Swiper(swiper, {
      modules: [Navigation, Pagination],
      loop: true,
      slidesPerView: 1,
      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
      },
      pagination: {
        el: paginationBullets,
        type: 'bullets',
        clickable: true,
      },
      breakpoints: {
        640: {
          loop: true,
          slidesPerView: 2,
          slidesPerGroup: 2,
          spaceBetween: 24,
        },
        1024: {
          loop: true,
          slidesPerView: 3,
          slidesPerGroup: 3,
          spaceBetween: 24,
        },
      },
      on: {
        slideChange(swiper) {
          const slidesPerGroup = swiper.params.slidesPerGroup ?? 1;
          const totalPages = Math.ceil(swiper.slides.length / slidesPerGroup);
          const currentPage = Math.floor(swiper.realIndex / slidesPerGroup) + 1;

          paginationFraction.innerHTML = `${currentPage} / ${totalPages}`;
        },
      },
    });
  },

  destroy() {
    this.swiper?.destroy(true, true);
  },
}));

Alpine.data('compareSlider', () => ({
  swiperImg: null,
  swiperInfo: null,

  init() {
    const swiperImg = this.$el.querySelector('.swiper-img');
    const swiperInfo = this.$el.querySelector('.swiper-info');
    const prevBtn = this.$el.querySelector('[data-nav-button="prev"]');
    const nextBtn = this.$el.querySelector('[data-nav-button="next"]');
    const paginationBullets = this.$el.querySelector('.pagination-bullets');
    const paginationFraction = this.$el.querySelector('.pagination-fraction');

    this.swiperInfo = new Swiper(swiperInfo, {
      modules: [Navigation, Pagination],
      loop: true,
      slidesPerView: 1,
      allowTouchMove: false,
    });

    this.swiperImg = new Swiper(swiperImg, {
      modules: [Controller, Navigation, Pagination],
      loop: true,
      slidesPerView: 1,
      allowTouchMove: false,
      longSwipes: false,
      shortSwipes: false,
      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
      },
      pagination: {
        el: paginationBullets,
        type: 'bullets',
        clickable: true,
      },
      controller: {
        control: this.swiperInfo,
      },
      on: {
        slideChange(swiper) {
          const totalSlides = swiper.slides.length;
          const currentSlide = swiper.realIndex + 1;

          paginationFraction.innerHTML = `${currentSlide} / ${totalSlides}`;
        },
      },
    });
  },

  destroy() {
    this.swiperImg?.destroy(true, true);
    this.swiperInfo?.destroy(true, true);
  },
}));

Alpine.data('compareItem', () => ({
  handlePos: 50,
  dragging: false,

  startDrag(event) {
    this.dragging = true;
    this.updateHandlePos(event);
  },

  drag(event) {
    if (!this.dragging) return;
    this.updateHandlePos(event);
  },

  updateHandlePos(event) {
    const rect = this.$el.getBoundingClientRect();
    const clientX = event.touches?.[0]?.clientX ?? event.changedTouches?.[0]?.clientX ?? event.clientX;
    const offsetX = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    this.handlePos = (offsetX / rect.width) * 100;
  },
}));

Alpine.data('vehicleList', () => ({
  activeTab: 'P',

  getItems(ref) {
    return this.$refs[ref] ? [...this.$refs[ref].querySelectorAll('div[x-data]')] : [];
  },

  openAll() {
    this.getItems(`items-${this.activeTab}`).forEach(item => Alpine.$data(item).open = true);
  },

  closeAll() {
    this.getItems(`items-${this.activeTab}`).forEach(item => Alpine.$data(item).open = false);
  },

  resetOpen() {
    Object.keys(this.$refs)
      .filter(key => key.startsWith('items-'))
      .flatMap(key => this.getItems(key))
      .forEach(item => Alpine.$data(item).open = false);
  },
}));

Alpine.data('blogListing', () => ({
  totalPosts: 0,
  totalPages: 0,
  currentPage: 1,
  perPage: 9,
  filteredPosts: [],
  categories: [],
  brands: [],
  terms: {},
  pages: [],
  isLoading: false,
  error: '',
  fetchTimer: null,
  controller: null,

  init() {
    this.terms = this.parseTerms(this.$el.dataset.terms);
    this.totalPosts = Number(this.$el.dataset.total) || 0;
    this.currentPage = Number(this.$el.dataset.page) || 1;
    this.$watch('categories', () => this.debouncedGetPosts(true));
    this.$watch('brands', () => this.debouncedGetPosts(true));
    this.getPosts();
  },

  // ponytail: setTimeout debounce only; no other debounce library.
  debouncedGetPosts(resetPage = false) {
    clearTimeout(this.fetchTimer);
    this.fetchTimer = setTimeout(() => {
      if (resetPage) {
        this.currentPage = 1;
      }
      this.getPosts();
    }, 300);
  },

  goToPage(page) {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.getPosts();
  },

  parseTerms(json) {
    try {
      return JSON.parse(json || '{}');
    } catch {
      return {};
    }
  },

  // ponytail: uses a throwaway textarea to decode entities and strip tags for titles.
  decodeHtml(html) {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = String(html || '');
    return textArea.value;
  },

  imageFromMedia(media) {
    if (!media) {
      return { src: '', width: '', height: '', alt: '' };
    }

    const sizes = media.media_details?.sizes ?? {};
    const thumb = sizes.medium_large || sizes.large || sizes.full || {
      source_url: media.source_url,
      width: media.media_details?.width,
      height: media.media_details?.height,
    };

    return {
      src: thumb.source_url || media.source_url || '',
      width: thumb.width || media.media_details?.width || '',
      height: thumb.height || media.media_details?.height || '',
      alt: media.alt_text || '',
    };
  },

  normalizePost(raw) {
    const media = raw._embedded?.['wp:featuredmedia']?.[0];
    const categoryIds = raw['blog-category'] ?? [];
    const brandIds = raw['brand'] ?? [];
    const badges = [...categoryIds, ...brandIds]
      .map((id) => this.terms[id])
      .filter(Boolean);

    return {
      id: raw.id,
      link: raw.link,
      title: this.decodeHtml(raw.title?.rendered),
      excerpt: raw.excerpt?.rendered ?? '',
      image: this.imageFromMedia(media),
      date: raw.date?.split('T')?.[0]?.replace(/-/g, '.') ?? '',
      badges,
    };
  },

  async getPosts() {
    this.isLoading = true;
    this.error = '';
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;

    const fetchURL = new URL(`${siteData.home_url}/wp-json/wp/v2/blog`);
    fetchURL.searchParams.set('per_page', String(this.perPage));
    fetchURL.searchParams.set('page', String(this.currentPage));
    fetchURL.searchParams.set('orderby', 'date');
    fetchURL.searchParams.set('order', 'desc');
    if (this.categories.length > 0) {
      fetchURL.searchParams.set('blog-category', this.categories.join(','));
    }
    if (this.brands.length > 0) {
      fetchURL.searchParams.set('brand', this.brands.join(','));
    }
    fetchURL.searchParams.set('_embed', 'wp:featuredmedia');
    fetchURL.searchParams.set('_fields', 'id,title,excerpt,link,blog-category,brand,date,acf,_links.wp:featuredmedia,_embedded');

    try {
      const response = await fetch(fetchURL.toString(), { signal: controller.signal });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      this.totalPosts = Number(response.headers.get('X-WP-Total')) || 0;
      this.totalPages = Number(response.headers.get('X-WP-TotalPages')) || 0;
      this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
      this.filteredPosts = data.map((post) => this.normalizePost(post));
    } catch (error) {
      if (error.name === 'AbortError' || this.controller !== controller) {
        return;
      }
      this.error = '記事を読み込めませんでした。';
      this.filteredPosts = [];
      this.totalPages = 0;
      this.pages = [];
    } finally {
      if (this.controller === controller) {
        this.isLoading = false;
      }
    }
  },
}));

Alpine.start();
